import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLoader } from "../../utils/LoaderUtils";
import { showSuccess, showError } from "../../utils/toastUtils";
import StoreAPI from "../../api/StoreApi";
import GlobalDeleteModal from "../../components/partials/GlobalDeleteModal";
import Pagination from "../../components/partials/Pagination";
import StoreRow from "./StoreRow";
import TableShimmer from "../../components/partials/TableShimmer";
import RowsPerPage from "../../components/partials/RowsPerPage";

function StoreList() {
  const [allStores, setAllStores] = useState("0");
  const [activeStores, setActiveStores] = useState("0");
  const [inactiveStores, setInactiveStores] = useState("0");

  const { showLoader, hideLoader } = useLoader();
  const [tableLoading, setTableLoading] = useState(false);

  const [storeData, setStoreData] = useState([]); // State for store list
  const [pagination, setPagination] = useState({}); // State for pagination info
  const [searchParams, setSearchParams] = useSearchParams();

  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* ================= FILTER DATA REQUIRED =================== */
  const query = React.useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
      status: searchParams.get("status") || null,
      search: searchParams.get("search") || null,
    }),
    [searchParams],
  );

  const searchTimeout = useRef();
  const handleSearch = (value) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateFilter("search", value);
    }, 400);
  };

  const updateFilter = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams);

      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }

      if (key !== "page") {
        params.set("page", 1);
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  /* ================= FETCH DATA =================== */
  const fetchStoreData = useCallback(async () => {
    setTableLoading(true);
    try {
      const res = await StoreAPI.getList(query);

      if (res.code === 200) {
        const { stores, pagination, total, activeStores, inactiveStores } =
          res.data;

        setStoreData(stores ?? []);
        setPagination(pagination ?? {});
        setAllStores(total);
        setActiveStores(activeStores);
        setInactiveStores(inactiveStores);
      }
    } catch (err) {
    } finally {
      setTableLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchStoreData();
  }, [fetchStoreData]);

  
  /* ================= STATUS CHANGE =================== */
  const handleStatusChange = async (storeId, newStatus) => {
    showLoader();

    try {
      const res = await StoreAPI.updateStatus(storeId);

      if (res.code === 200) {
        setStoreData((prev) => {
          let oldStatus = null;

          const updated = prev.map((store) => {
            if (store._id === storeId) {
              oldStatus = store.status;
              return { ...store, status: newStatus };
            }
            return store;
          });

          if (oldStatus !== newStatus) {
            if (oldStatus === "active") setActiveStores((p) => Number(p) - 1);
            if (oldStatus === "deactive")
              setInactiveStores((p) => Number(p) - 1);

            if (newStatus === "active") setActiveStores((p) => Number(p) + 1);
            if (newStatus === "deactive")
              setInactiveStores((p) => Number(p) + 1);
          }

          return updated;
        });

        showSuccess("Status updated successfully");
      } else {
        showError("Failed to update status");
      }
    } catch (err) {
      showError("Something went wrong");
    } finally {
      hideLoader();
    }
  };

  /* ================= DELETE FLOW =================== */
  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);

      const res = await StoreAPI.deleteItem(deleteId);

      if (res.code === 200) {
        showSuccess(res.message);

        setStoreData((prev) => prev.filter((s) => s._id !== deleteId));
        setAllStores((p) => p - 1);

        if (storeData.length === 1 && query.page > 1) {
          setSearchParams({ ...query, page: query.page - 1 });
        }

        document.querySelector("#delete-modal .btn-close").click();
      } else {
        showError(res.message);
      }
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  /* ================= COPY PASTE =================== */
  const copyText = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      showSuccess("Copied");
    } catch {
      showError("Copy failed");
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>All Stores</h4>
              <h6>Keep track and manage your stores efficiently</h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to="/stores/add-new/general" className="btn btn-primary">
              <i className="ti ti-circle-plus me-1" />
              Add Store
            </Link>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-4 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-primary flex-shrink-0">
                    <i className="ti ti-building fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      Total Stores
                    </p>
                    <h4>{allStores}</h4>
                  </div>
                </div>
                <div id="total-chart" />
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-success flex-shrink-0">
                    <i className="ti ti-building fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      Active Stores
                    </p>
                    <h4>{activeStores}</h4>
                  </div>
                </div>
                <div id="active-chart" />
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-danger flex-shrink-0">
                    <i className="ti ti-building fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      Inactive Stores
                    </p>
                    <h4>{inactiveStores}</h4>
                  </div>
                </div>
                <div id="inactive-chart" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text" id="basic-addon1">
                    <i className="ti ti-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search By Store Name/Email Address/Phone Number"
                    defaultValue={query.search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-6" style={{ justifyItems: "right" }}>
                <div className="d-flex table-dropdown my-xl-auto right-content align-items-center row-gap-3">
                  <select
                    className="form-control ms-2"
                    value={searchParams.get("status") || ""}
                    onChange={(e) => updateFilter("status", e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="deactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="row">
              <div className="col-md-12">
                {pagination && (
                  <div className="countablepagination p-2">
                    <p>
                      Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                      {Math.min(pagination.page * pagination.limit, allStores)}{" "}
                      of {allStores} results
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="table-responsive">
              <table className="table">
                <thead className="thead-light">
                  <tr>
                    <th>S. No.</th>
                    <th>Store</th>
                    <th>Website Url</th>
                    <th>Contact Details</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <TableShimmer rows={query.limit} cols={7} />
                  ) : storeData.length > 0 ? (
                    storeData.map((store, index) => (
                      <StoreRow
                        key={store._id}
                        store={store}
                        index={index}
                        page={query.page}
                        limit={query.limit}
                        onStatusChange={handleStatusChange}
                        onDelete={handleOpenDeleteModal}
                        onCopy={copyText}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center p-4 text-muted">
                        No stores found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="row">
              <RowsPerPage
                value={searchParams.get("limit") || "10"}
                onChange={(val) => updateFilter("limit", val)}
              />

              <Pagination pagination={pagination} updateFilter={updateFilter} />
            </div>
          </div>
        </div>
      </div>

      <GlobalDeleteModal onDeleted={handleDelete} isDeleting={isDeleting} />
    </>
  );
}

export default StoreList;
